import { useState } from "react";
import { Lock, LockOpen, CalendarDays, ShieldAlert, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { setDocumentSeal } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export const SEAL_MODES = [
  { id: "open", label: "Open now", hint: "Everyone in the vault can play it today.", Icon: LockOpen },
  { id: "date", label: "Sealed until a date", hint: "Hidden from family until the day you pick — a birthday, a graduation.", Icon: CalendarDays },
  { id: "verified", label: "Opens right away after an ID check", hint: "No waiting period — your healthcare agent (or, if none is named, your executor) sees it the moment their ID is verified. For medical directives, healthcare POA, HIPAA release.", Icon: BadgeCheck },
  { id: "emergency", label: "Sealed until emergency access", hint: "Only you can see it until break-glass access is granted.", Icon: ShieldAlert },
  { id: "both", label: "Whichever comes first", hint: "Opens on the date, or sooner if emergency access is granted.", Icon: Lock },
];

export const sealLabel = (seal) => {
  const m = SEAL_MODES.find((x) => x.id === (seal?.mode || "open"));
  if (!m || m.id === "open") return "";
  const d = seal.until || "a date";
  return m.id === "date" ? `Sealed until ${d}` : m.id === "both" ? `Sealed until ${d} or emergency` : m.id === "verified" ? "Opens after an ID check" : "Sealed until emergency access";
};

export const SealPicker = ({ value, onChange, compact = false }) => (
  <div className="grid gap-1.5" data-testid="seal-picker">
    {SEAL_MODES.map(({ id, label, hint, Icon }) => (
      <button key={id} type="button" onClick={() => onChange({ ...value, mode: id })} data-testid={`seal-mode-${id}`}
        className={`text-left rounded-xl border px-3 py-2 flex items-start gap-3 transition-colors ${value.mode === id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
        <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
        <span><span className="text-sm font-medium block">{label}</span>{!compact && <span className="text-xs text-muted-foreground">{hint}</span>}</span>
      </button>
    ))}
    {(value.mode === "date" || value.mode === "both") && (
      <Input type="date" value={value.until || ""} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(e) => onChange({ ...value, until: e.target.value })} className="h-9" data-testid="seal-until-input" />
    )}
  </div>
);

export const ReleaseRules = ({ doc, onSaved }) => {
  const [seal, setSeal] = useState({ mode: doc.seal?.mode || "open", until: doc.seal?.until || "", to_name: doc.seal?.to_name || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await setDocumentSeal(doc.id, seal);
      toast.success(r.seal.mode === "open" ? "Open to the family" : `Sealed · ${r.label}`);
      onSaved?.({ ...doc, seal: r.seal });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const dirty = seal.mode !== (doc.seal?.mode || "open") || (seal.until || "") !== (doc.seal?.until || "") || (seal.to_name || "") !== (doc.seal?.to_name || "");
  return (
    <section className="mt-6" data-testid="release-rules">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2"><Lock className="h-3.5 w-3.5" /> Release rules</div>
      <Input value={seal.to_name} onChange={(e) => setSeal({ ...seal, to_name: e.target.value })} placeholder="For whom? (optional)" className="h-9 mb-2" data-testid="seal-to-name" />
      <SealPicker value={seal} onChange={setSeal} compact />
      <div className="flex justify-end mt-2">
        <Button size="sm" className="rounded-full" disabled={busy || !dirty || ((seal.mode === "date" || seal.mode === "both") && !seal.until)} onClick={save} data-testid="seal-save">Save release rule</Button>
      </div>
    </section>
  );
};
